import React from 'react'
import clsx from 'clsx'
import {
  PaginationState,
  SortingState,
  ColumnSizingState,
  Updater,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'

import { TableProps } from './types'

import Pagination from './Pagination'
import InfiniteLoaderNavButtons from './Pagination/NavButtons/InfiniteLoaderNavButtons'
import TableHeadersComponent from './Components/TableHeadersComponent'
import TableBodyComponent from './Components/TableBodyComponent'
import HeaderComponent from './Components/HeaderComponent'
import TopRowComponent from './Components/TopRowComponent'
import BottomRowComponent from './Components/BottomRowComponent'

import { DEFAULT_PAGINATION } from './helpers'
import useSavedValue from '../../../hooks/useSavedValue'
import useStyles from './styles'

const Table = <T extends unknown>({
  data,
  columns,
  children,
  filter,
  renderRowSubComponent,
  onRowClicked,
  paginationOptions = DEFAULT_PAGINATION,
  shouldRenderInfinitePagination = false,
  isLoadingMore = false,
  infintePaginationLoad,
  reachedEnd,
  enableSort,
  disablePagination,
  canExpandRow,
  expanded = {},
  onToggleExpanded = () => {},
  message,
  loaded = true,
  hideCount,
  hideHeader,
  variant,
  getRowClass,
  disableSkeleton,

  // For pagination
  onSetPageSize,
  onPageChange,
  showPagination,
  pageNum,
  numResults,
  manualPagination,
  BodyWrapper = React.Fragment,
  bodyRef,
  isSmall,
  padBottom,
  disableHeader,
  id = '',
  pagesize = 25,
  hidePaginationPage,
  useManualSort = false,
  setTableSort,
  disableSortRemove = false,

  // For scrolling
  targetID,
  targetRef,

  // For blinking
  target,

  // For sorting
  allowDragDropSort,
  onDragDrop,
  dragDropID,
  alternateDropCallback = false,
  initialSort = [],

  enableResizing = false,
  enableCustomContextMenu = false,

  stickyHeader,
  tableHeight,
  estimatedRowHeight
}: TableProps<T>) => {
  let wrapperRef = React.useRef(null)
  if (bodyRef) {
    wrapperRef = bodyRef
  }

  const classes = useStyles({ variant, loaded })

  const [{ pageIndex, pageSize }, setPagination] =
    useSavedValue<PaginationState>({
      key: `table_pagination_${id}`,
      defaultValue: `{"pageIndex":0,"pageSize":${
        pagesize ?? paginationOptions[0] ?? 25
      }}`
    })
  const [sorting, setSorting] = React.useState<SortingState>(initialSort)
  const [columnSizing, setColumnSizing] = useSavedValue<ColumnSizingState>({
    key: `table_sizing_${id}`,
    defaultValue: '{}'
  })
  const pagination = React.useMemo<PaginationState>(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize]
  )
  const tableWrapper = React.useRef<HTMLDivElement>(null)

  // Update pagination size
  React.useEffect(() => {
    if (manualPagination && onSetPageSize) {
      onSetPageSize(pageSize)
    }
  }, [manualPagination, onSetPageSize, pageSize])

  // Update pagination index
  React.useEffect(() => {
    if (manualPagination && onPageChange) {
      onPageChange(pageIndex)
    }
  }, [manualPagination, onPageChange, pageIndex])

  // Update sorting
  React.useEffect(() => {
    setTableSort && setTableSort(sorting)
    setPagination(old => ({ ...old, pageIndex: 0 }))
  }, [setTableSort, sorting, setPagination])

  // Scroll to top on page change
  const handleScroll = React.useCallback(() => {
    if (!stickyHeader && wrapperRef.current) {
      wrapperRef.current.scrollIntoView()
      setTimeout(() => {
        if (wrapperRef.current) {
          wrapperRef.current.scrollIntoView({ behavior: 'smooth' })
        }
      }, 0) //1000)
    }
  }, [stickyHeader])

  const handlePaginationChange = React.useCallback(
    (fn: Updater<PaginationState>) => {
      // @ts-ignore
      const newVal = fn(pagination) as PaginationState

      if (newVal.pageIndex !== pagination.pageIndex) {
        handleScroll()
      }

      setPagination(newVal)
    },
    [pagination, handleScroll, setPagination]
  )

  const handleSortingChange = React.useCallback(
    (fn: Updater<SortingState>) => {
      // @ts-ignore
      const newVal = fn(sorting) as SortingState

      // Remove sorting when clicking on the same column twice
      if (newVal[0].id === sorting[0]?.id && newVal[0].desc === true) {
        setSorting([])
        return
      }

      setSorting(newVal)
    },
    [sorting]
  )

  const table = useReactTable({
    data,
    columns,

    // Row representation
    getCoreRowModel: getCoreRowModel<T>(),
    getPaginationRowModel: getPaginationRowModel<T>(),
    getSortedRowModel: getSortedRowModel<T>(),

    // Pagination options
    manualPagination: manualPagination || disablePagination,
    pageCount: -1,
    onPaginationChange: handlePaginationChange,

    // Sorting options
    enableSorting: enableSort,
    manualSorting: useManualSort,
    enableSortingRemoval: !disableSortRemove,
    sortDescFirst: true,
    onSortingChange: handleSortingChange,

    // Resizing
    enableColumnResizing: enableResizing,
    columnResizeMode: 'onChange',
    onColumnSizingChange: setColumnSizing,

    // Table state
    state: {
      pagination,
      sorting,
      columnSizing
    }
  })

  // Calculate default column width
  React.useEffect(() => {
    // Ignore non-defaults
    if (Object.keys(columnSizing).length !== 0 || columns.length === 0) {
      return
    }

    const elem = tableWrapper.current
    if (!elem) {
      return
    }

    const { width } = elem.getBoundingClientRect()
    const size = Math.round(width / columns.length)

    const defaults: Record<string, number> = {}
    columns.forEach(column => {
      // @ts-ignore
      defaults[column.accessorKey] = size
    })
    setColumnSizing(defaults)
  })

  const shouldRenderPagination = React.useMemo(() => {
    // Hard lock to hide
    if (disablePagination) {
      return false
    }

    // Forces show even if there shouldn't be extra pages yet
    // may be used for async pagination
    if (showPagination) {
      return true
    }

    if (pageSize > 25) {
      return true
    }

    return data.length > pageSize
  }, [disablePagination, showPagination, data.length, pageSize])

  React.useEffect(() => {
    table.setGlobalFilter(filter || '')
  }, [table, filter])

  return (
    <div className={clsx(classes.root, 'table-root')}>
      {children && <div>{children}</div>}

      {shouldRenderPagination && isSmall && (
        <Pagination
          table={table}
          paginationOptions={paginationOptions}
          isSmall
          hidePaginationPage={hidePaginationPage}
          manualPagination={manualPagination}
          numResults={numResults}
          pageNum={pageNum}
          pagination={pagination}
          setPagination={setPagination}
        />
      )}

      <div
        id={id}
        ref={elem => {
          if (wrapperRef) {
            wrapperRef.current = elem
          }
          tableWrapper.current = elem
        }}
        className='wrapper'
        style={stickyHeader ? { height: tableHeight, overflowY: 'auto' } : {}}
      >
        <table
          className='table'
          style={{ width: enableResizing ? table.getTotalSize() : '100%' }}
        >
          <TableHeadersComponent
            table={table}
            disableHeader={disableHeader}
            hideHeader={hideHeader}
            manualPagination={manualPagination}
            numResults={numResults}
            hideCount={hideCount}
            allowDragDropSort={allowDragDropSort}
            stickyHeader={stickyHeader}
            enableResizing={enableResizing}
          />

          <TableBodyComponent
            table={table}
            loaded={loaded}
            message={message}
            BodyWrapper={BodyWrapper}
            expanded={expanded}
            targetID={targetID}
            targetRef={targetRef}
            target={target}
            onRowClicked={onRowClicked}
            getRowClass={getRowClass}
            renderRowSubComponent={renderRowSubComponent}
            onToggleExpanded={onToggleExpanded}
            canExpandRow={canExpandRow}
            disableSkeleton={disableSkeleton}
            allowDragDropSort={allowDragDropSort}
            onDragDrop={onDragDrop}
            dragDropID={dragDropID}
            alternateDropCallback={alternateDropCallback}
            stickyHeader={stickyHeader}
            wrapperRef={wrapperRef}
            estimatedRowHeight={estimatedRowHeight}
            enableResizing={enableResizing}
            enableCustomContextMenu={enableCustomContextMenu}
          />
        </table>
      </div>

      {shouldRenderPagination && (
        <Pagination
          table={table}
          paginationOptions={paginationOptions}
          isSmall={isSmall}
          hidePaginationPage={hidePaginationPage}
          manualPagination={manualPagination}
          numResults={numResults}
          pageNum={pageNum}
          pagination={pagination}
          setPagination={setPagination}
        />
      )}

      {shouldRenderInfinitePagination && (
        <InfiniteLoaderNavButtons
          // @ts-ignore
          loadMore={infintePaginationLoad}
          disabled={reachedEnd}
          isSmall={isSmall}
          isLoading={isLoadingMore}
        />
      )}

      {padBottom && <div style={{ height: '1rem' }} />}
    </div>
  )
}

Table.Header = HeaderComponent
Table.TopRow = TopRowComponent
Table.BottomRow = BottomRowComponent

export default Table
